import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { io } from 'socket.io-client';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2, Trophy, Zap, Coins, Volume2, VolumeX, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../providers/AuthProvider';
import { usePriceOracle } from '../providers/PriceOracleProvider';
import { useCardBuff } from '../providers/CardBuffProvider';
import { useGameWallet } from '../providers/GameWalletProvider';
import WagerCard from '../components/WagerCard';
import LivePriceDisplay from '../components/LivePriceDisplay';
import VictoryScreen from '../components/VictoryScreen';
import BuffHUD from '../components/BuffHUD';
import audioManager from '../lib/AudioManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Canvas will be dynamically sized - these are the base logical dimensions
const BASE_WIDTH = 800;
const BASE_HEIGHT = 500;
const ENTRY_FEE = 100;

// Target visuals - Updated with categories and NEW NEON ORB STYLES
const TARGET_VISUALS = {
  gem: { emoji: '💎', color: '#00FF99', glowColor: '#00FF99', category: 'good', orbStyle: 'emerald' },
  rocket: { emoji: '🚀', color: '#FF6B00', glowColor: '#FF9933', category: 'good', orbStyle: 'emerald' },
  pepe_coin: { emoji: '🐸', color: '#00FF00', glowColor: '#33FF33', category: 'good', orbStyle: 'emerald' },
  diamond: { emoji: '💠', color: '#00CCFF', glowColor: '#66DDFF', category: 'good', orbStyle: 'emerald' },
  sec_logo: { emoji: '🏛️', color: '#FF0000', glowColor: '#FF3333', category: 'bad', orbStyle: 'danger' },
  gary_gensler: { emoji: '👴', color: '#FF0000', glowColor: '#FF4444', category: 'bad', orbStyle: 'danger' },
  red_candle: { emoji: '📉', color: '#FF3333', glowColor: '#FF5555', category: 'bad', orbStyle: 'danger' },
  rug_pull: { emoji: '💀', color: '#FF00FF', glowColor: '#FF66FF', category: 'freeze', orbStyle: 'freeze' },
  golden_elon: { emoji: '⭐', color: '#FFD700', glowColor: '#FFEA00', category: 'jackpot', orbStyle: 'gold' },
};

// Shatter Particle for death effect
class ShatterParticle {
  constructor(x, y, color, angle) {
    this.x = x;
    this.y = y;
    const speed = 4 + Math.random() * 8;
    this.vx = Math.cos(angle) * speed + (Math.random() - 0.5) * 3;
    this.vy = Math.sin(angle) * speed + (Math.random() - 0.5) * 3;
    this.life = 1;
    this.decay = 0.025 + Math.random() * 0.02;
    this.color = color;
    this.size = 3 + Math.random() * 6;
    this.rotation = Math.random() * Math.PI * 2;
    this.rotationSpeed = (Math.random() - 0.5) * 0.3;
  }
  update() {
    this.x += this.vx;
    this.y += this.vy;
    this.vy += 0.15; // Gravity
    this.vx *= 0.98;
    this.life -= this.decay;
    this.rotation += this.rotationSpeed;
  }
  draw(ctx, dpr) {
    if (this.life <= 0) return;
    ctx.save();
    ctx.globalAlpha = Math.max(0, this.life);
    ctx.translate(this.x * dpr, this.y * dpr);
    ctx.rotate(this.rotation);
    
    // Glass shard shape
    ctx.fillStyle = this.color;
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 8 * dpr;
    ctx.beginPath();
    const s = this.size * this.life * dpr;
    ctx.moveTo(0, -s);
    ctx.lineTo(s * 0.6, s * 0.5);
    ctx.lineTo(-s * 0.6, s * 0.5);
    ctx.closePath();
    ctx.fill();
    
    ctx.restore();
  }
}

// Score Particle - flies UP towards score counter (DPR-aware)
class ScoreParticle {
  constructor(x, y, color, text, targetY = 50) {
    this.x = x;
    this.y = y;
    this.targetY = targetY;
    this.vx = (Math.random() - 0.5) * 4;
    this.vy = -8 - Math.random() * 4;
    this.life = 1;
    this.decay = 0.015;
    this.color = color;
    this.text = text;
    this.scale = 1;
  }
  update() {
    this.x += this.vx;
    this.y += this.vy;
    this.vy *= 0.96;
    this.life -= this.decay;
    this.scale = 1 + (1 - this.life) * 0.5;
  }
  draw(ctx, dpr = 1) {
    if (this.life <= 0) return;
    ctx.globalAlpha = Math.max(0, this.life);
    ctx.font = `bold ${Math.floor(28 * this.scale * dpr)}px Anton`;
    ctx.fillStyle = this.color;
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 4 * dpr;
    ctx.textAlign = 'center';
    ctx.strokeText(this.text, this.x * dpr, this.y * dpr);
    ctx.fillText(this.text, this.x * dpr, this.y * dpr);
    ctx.globalAlpha = 1;
  }
}

// Explosion Particle (DPR-aware)
class ExplosionParticle {
  constructor(x, y, color) {
    this.x = x;
    this.y = y;
    this.vx = (Math.random() - 0.5) * 12;
    this.vy = (Math.random() - 0.5) * 12;
    this.life = 1;
    this.decay = 0.03 + Math.random() * 0.02;
    this.color = color;
    this.size = 4 + Math.random() * 6;
  }
  update() {
    this.x += this.vx;
    this.y += this.vy;
    this.vx *= 0.95;
    this.vy *= 0.95;
    this.life -= this.decay;
  }
  draw(ctx, dpr = 1) {
    if (this.life <= 0) return;
    ctx.globalAlpha = Math.max(0, this.life);
    ctx.fillStyle = this.color;
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 10 * dpr;
    ctx.beginPath();
    ctx.arc(this.x * dpr, this.y * dpr, Math.max(0, this.size * this.life * dpr), 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
  }
}

const DegenSlap = () => {
  const navigate = useNavigate();
  const { user, refreshUser, localWallet } = useAuth();
  const { arenaCosts } = usePriceOracle();
  // CRITICAL FIX: Get blockchain balance and refresh function directly
  const { slapBalance, fetchSlapTokenBalance } = useGameWallet();
  const { 
    initializeGameBuffs, 
    useShield: consumeShield, 
    applyScoreMultiplier, 
    getSlowMoFactor, 
    getBuffDisplayInfo,
    equippedCard 
  } = useCardBuff();
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const socketRef = useRef(null);
  const animationRef = useRef(null);
  const targetsRef = useRef({});
  const particlesRef = useRef([]);
  const shakeRef = useRef({ intensity: 0 });
  const frozenUntilRef = useRef(0);
  const gameActiveRef = useRef(false);
  const botScoresRef = useRef({ bot1: 0, bot2: 0, bot3: 0 });
  const scoreRef = useRef(0);
  const goldenElonAngleRef = useRef(0);
  const gridOffsetRef = useRef(0); // For animated grid
  const canvasSizeRef = useRef({ width: BASE_WIDTH, height: BASE_HEIGHT, dpr: 1 });
  
  // Card Buff refs
  const activeBuffRef = useRef(null);
  const slowMoFactorRef = useRef(1.0);
  
  // Opponent Ghost View refs
  const ghostHitsRef = useRef([]); // { x, y, time, type: 'hit'|'miss'|'bomb', color }
  const opponentFlashRef = useRef({ active: false, color: '', until: 0 });
  
  // Progressive Difficulty state
  const spawnRateRef = useRef(600); // Current spawn rate in ms
  const difficultyPhaseRef = useRef('normal'); // 'normal' | 'escalation' | 'doom'

  const [gameState, setGameState] = useState('lobby');
  const [showTutorial, setShowTutorial] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [score, setScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [playerCount, setPlayerCount] = useState(0);
  const [pot, setPot] = useState(0);
  const [result, setResult] = useState(null);
  const [connected, setConnected] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isFrozen, setIsFrozen] = useState(false);
  const [claimingShards, setClaimingShards] = useState(false);
  const [difficultyPhase, setDifficultyPhase] = useState('normal');
  const [showVictoryScreen, setShowVictoryScreen] = useState(false);
  const [buffDisplayInfo, setBuffDisplayInfo] = useState(null);

  const username = user?.wallet_address?.slice(0, 8) || 'Player';
  // CRITICAL FIX: Use BLOCKCHAIN balance (slapBalance), NOT server data (user?.shards)
  // This was the bug causing "NEED MORE $SLAP" with 15M tokens
  const userDegen = slapBalance || 0;
  const sessionId = user?.wallet_address || 'default_session';
  
  // Get dynamic entry fee from price oracle
  const currentEntryFee = arenaCosts?.casual || ENTRY_FEE;

  // Toggle sound and update audioManager
  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const newValue = !prev;
      audioManager.toggle(newValue);
      return newValue;
    });
  }, []);

  // Simulate opponent (bot) hit for Ghost View
  const simulateOpponentHit = useCallback((hitType = 'hit') => {
    const { width } = canvasSizeRef.current;
    const ghostX = Math.random() * (width - 100) + 50;
    const ghostY = Math.random() * 80 + 30; // Top area only
    
    const colors = {
      hit: '#00FF88',
      miss: '#FF4444',
      bomb: '#FF00FF'
    };
    
    ghostHitsRef.current.push({
      x: ghostX,
      y: ghostY,
      time: Date.now(),
      type: hitType,
      color: colors[hitType] || '#00FF88'
    });
    
    // Flash the opponent area if they hit a bomb
    if (hitType === 'bomb') {
      opponentFlashRef.current = {
        active: true,
        color: '#FF0000',
        until: Date.now() + 500
      };
    }
  }, []);

  // Claim free test $SLAP
  const claimTestDegen = async () => {
    setClaimingShards(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/user/claim-test-shards?session_id=${sessionId}`);
      if (response.data.success) {
        toast.success(`+${response.data.shards_added} $SLAP!`, {
          description: `New balance: ${response.data.new_balance} $SLAP`
        });
        await refreshUser();
      }
    } catch (err) {
      toast.error('Failed to claim $SLAP');
      console.error(err);
    } finally {
      setClaimingShards(false);
    }
  };

  // Keep scoreRef in sync
  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  // Socket connection (with fallback)
  useEffect(() => {
    socketRef.current = io(BACKEND_URL, {
      path: '/socket.io/',
      transports: ['polling', 'websocket'],
      reconnectionAttempts: 2,
      timeout: 3000,
    });

    const timeout = setTimeout(() => {
      if (!connected) {
        setConnected(true);
        toast.info('Offline Mode - Playing vs Bots');
      }
    }, 3000);

    socketRef.current.on('connect', () => {
      setConnected(true);
      clearTimeout(timeout);
    });

    return () => {
      clearTimeout(timeout);
      socketRef.current?.disconnect();
    };
  }, [connected]);

  // ============================================================
  // HIGH-FIDELITY CANVAS RENDERING ENGINE
  // ============================================================
  useEffect(() => {
    if (gameState !== 'playing' && gameState !== 'finished') return;
    
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    
    const ctx = canvas.getContext('2d', { alpha: false });
    
    // ---- RESIZE OBSERVER: Fix distortion with DPR scaling ----
    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      
      // Set canvas buffer size (actual pixels)
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      
      // Set display size (CSS pixels)
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      
      // Store for coordinate calculations
      canvasSizeRef.current = {
        width: rect.width,
        height: rect.height,
        dpr: dpr
      };
    };
    
    const resizeObserver = new ResizeObserver(resizeCanvas);
    resizeObserver.observe(container);
    resizeCanvas(); // Initial size

    // ---- HELPER: Draw Glassmorphism Orb ----
    const drawGlassOrb = (x, y, radius, color, glowColor, orbStyle, spawnProgress = 1, pulsePhase = 0) => {
      const { dpr } = canvasSizeRef.current;
      const px = x * dpr;
      const py = y * dpr;
      const baseR = radius * dpr * spawnProgress;
      
      // Early return if radius too small
      if (baseR < 1) return 0;
      
      ctx.save();
      
      // Elastic spawn animation (0 -> 1.2 -> 1.0)
      const elasticScale = spawnProgress < 1 
        ? Math.sin(spawnProgress * Math.PI * 1.5) * 1.2 
        : 1 + Math.sin(pulsePhase) * 0.05;
      const finalR = Math.max(1, baseR * Math.max(0.1, elasticScale));
      
      // OUTER GLOW (Neon effect)
      const glowLayers = orbStyle === 'danger' ? 4 : 3;
      for (let i = glowLayers; i >= 0; i--) {
        const glowR = Math.max(1, finalR * (1.3 + i * 0.15));
        const gradient = ctx.createRadialGradient(px, py, 0, px, py, glowR);
        const alpha = orbStyle === 'danger' 
          ? (0.4 - i * 0.08) * (1 + Math.sin(pulsePhase * 3) * 0.3) // Aggressive pulse for danger
          : (0.25 - i * 0.06);
        gradient.addColorStop(0, `${glowColor}00`);
        gradient.addColorStop(0.5, `${glowColor}${Math.floor(Math.max(0, alpha) * 255).toString(16).padStart(2, '0')}`);
        gradient.addColorStop(1, `${glowColor}00`);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(px, py, glowR, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // GLASS BUBBLE BASE
      const baseGradient = ctx.createRadialGradient(
        px - finalR * 0.3, py - finalR * 0.3, 0,
        px, py, Math.max(1, finalR)
      );
      
      if (orbStyle === 'emerald') {
        // Green emerald orb
        baseGradient.addColorStop(0, '#FFFFFF');
        baseGradient.addColorStop(0.1, `${color}EE`);
        baseGradient.addColorStop(0.5, `${color}CC`);
        baseGradient.addColorStop(0.8, `${color}99`);
        baseGradient.addColorStop(1, `${color}66`);
      } else if (orbStyle === 'danger') {
        // Red danger orb with spiky feel
        baseGradient.addColorStop(0, '#FFAAAA');
        baseGradient.addColorStop(0.2, '#FF4444');
        baseGradient.addColorStop(0.6, '#CC0000');
        baseGradient.addColorStop(1, '#660000');
      } else if (orbStyle === 'freeze') {
        // Purple freeze orb
        baseGradient.addColorStop(0, '#FFFFFF');
        baseGradient.addColorStop(0.2, '#FF66FF');
        baseGradient.addColorStop(0.6, '#CC00CC');
        baseGradient.addColorStop(1, '#660066');
      }
      
      ctx.fillStyle = baseGradient;
      ctx.beginPath();
      ctx.arc(px, py, finalR, 0, Math.PI * 2);
      ctx.fill();
      
      // GLASS HIGHLIGHT (top-left shine)
      if (finalR > 5) {
        const highlightGradient = ctx.createRadialGradient(
          px - finalR * 0.4, py - finalR * 0.4, 0,
          px - finalR * 0.2, py - finalR * 0.2, Math.max(1, finalR * 0.6)
        );
        highlightGradient.addColorStop(0, 'rgba(255,255,255,0.9)');
        highlightGradient.addColorStop(0.3, 'rgba(255,255,255,0.4)');
        highlightGradient.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = highlightGradient;
        ctx.beginPath();
        ctx.arc(px - finalR * 0.3, py - finalR * 0.3, finalR * 0.5, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // BORDER RING
      ctx.strokeStyle = orbStyle === 'danger' ? '#FF0000' : orbStyle === 'freeze' ? '#FF00FF' : '#00FF00';
      ctx.lineWidth = 2 * dpr;
      ctx.shadowColor = ctx.strokeStyle;
      ctx.shadowBlur = 8 * dpr;
      ctx.beginPath();
      ctx.arc(px, py, finalR, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
      
      ctx.restore();
      
      return finalR / dpr; // Return actual radius for emoji sizing
    };
    
    // ---- HELPER: Draw Gold Coin (Jackpot) ----
    const drawGoldCoin = (x, y, radius, rotationAngle, spawnProgress = 1) => {
      const { dpr } = canvasSizeRef.current;
      const px = x * dpr;
      const py = y * dpr;
      const r = Math.max(1, radius * dpr * spawnProgress);
      
      // Early return if too small
      if (r < 5) return r / dpr;
      
      ctx.save();
      
      // Spinning coin effect - squash based on rotation
      const squash = Math.abs(Math.cos(rotationAngle));
      const coinWidth = Math.max(1, r * (0.3 + squash * 0.7));
      
      // Massive outer glow
      for (let i = 5; i >= 0; i--) {
        const glowR = Math.max(1, r * (2 + i * 0.3));
        const alpha = Math.max(0, 0.15 - i * 0.02);
        ctx.fillStyle = `rgba(255, 215, 0, ${alpha})`;
        ctx.beginPath();
        ctx.ellipse(px, py, glowR, glowR * 0.9, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Spinning rays
      ctx.translate(px, py);
      ctx.rotate(rotationAngle);
      for (let i = 0; i < 12; i++) {
        ctx.rotate(Math.PI / 6);
        ctx.fillStyle = 'rgba(255, 215, 0, 0.4)';
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(-r * 0.15, -r * 2.5);
        ctx.lineTo(r * 0.15, -r * 2.5);
        ctx.closePath();
        ctx.fill();
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform
      
      // Coin body with 3D effect
      const coinGradient = ctx.createLinearGradient(px - coinWidth, py, px + coinWidth, py);
      coinGradient.addColorStop(0, '#B8860B');
      coinGradient.addColorStop(0.2, '#FFD700');
      coinGradient.addColorStop(0.5, '#FFFACD');
      coinGradient.addColorStop(0.8, '#FFD700');
      coinGradient.addColorStop(1, '#B8860B');
      
      ctx.fillStyle = coinGradient;
      ctx.beginPath();
      ctx.ellipse(px, py, coinWidth, r, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Coin edge highlight
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 3 * dpr;
      ctx.shadowColor = '#FFD700';
      ctx.shadowBlur = 20 * dpr;
      ctx.stroke();
      ctx.shadowBlur = 0;
      
      // Inner circle detail
      ctx.strokeStyle = '#B8860B';
      ctx.lineWidth = 2 * dpr;
      ctx.beginPath();
      ctx.ellipse(px, py, coinWidth * 0.7, r * 0.7, 0, 0, Math.PI * 2);
      ctx.stroke();
      
      ctx.restore();
      
      return r / dpr;
    };
    
    // ---- HELPER: Draw Retro-Wave Grid Background ----
    const drawRetroGrid = () => {
      const { width, height, dpr } = canvasSizeRef.current;
      const w = width * dpr;
      const h = height * dpr;
      
      // Animate grid offset
      gridOffsetRef.current = (gridOffsetRef.current + 0.5) % 50;
      const offset = gridOffsetRef.current * dpr;
      
      // Vertical lines (stationary)
      ctx.strokeStyle = '#00FFFF15';
      ctx.lineWidth = 1 * dpr;
      const gridSpacing = 40 * dpr;
      
      for (let x = 0; x < w; x += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      
      // Horizontal lines (moving towards viewer - creates depth)
      const horizonY = h * 0.3; // Horizon line at 30%
      const numLines = 20;
      
      for (let i = 0; i < numLines; i++) {
        const progress = ((i / numLines) + offset / (50 * dpr)) % 1;
        const y = horizonY + (h - horizonY) * Math.pow(progress, 1.5);
        const alpha = Math.floor(progress * 40 + 10);
        
        ctx.strokeStyle = `#FF00FF${alpha.toString(16).padStart(2, '0')}`;
        ctx.lineWidth = (1 + progress * 2) * dpr;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }
      
      // Perspective lines from center-bottom to horizon
      ctx.strokeStyle = '#00FFFF10';
      ctx.lineWidth = 1 * dpr;
      const centerX = w / 2;
      for (let i = -10; i <= 10; i++) {
        const spread = (i / 10) * w * 0.8;
        ctx.beginPath();
        ctx.moveTo(centerX, horizonY);
        ctx.lineTo(centerX + spread, h);
        ctx.stroke();
      }
    };

    // ---- MAIN RENDER LOOP ----
    const render = () => {
      const { width, height, dpr } = canvasSizeRef.current;
      const w = width * dpr;
      const h = height * dpr;
      const now = Date.now();
      
      // Calculate opponent zone and player zone
      const vsBarY = h * 0.25; // VS bar at 25% from top
      const opponentZoneH = vsBarY;
      const playerZoneY = vsBarY + 30 * dpr; // Player area starts below VS bar
      
      // Apply screen shake (DOOM MODE has constant slight shake)
      const doomShake = difficultyPhaseRef.current === 'doom' ? 2 : 0;
      const totalShake = shakeRef.current.intensity + doomShake;
      if (totalShake > 0) {
        ctx.save();
        ctx.translate(
          (Math.random() - 0.5) * totalShake * dpr,
          (Math.random() - 0.5) * totalShake * dpr
        );
        if (shakeRef.current.intensity > 0) shakeRef.current.intensity *= 0.9;
      }

      // BACKGROUND: Deep space black with subtle gradient
      const bgGradient = ctx.createRadialGradient(w/2, h/2, 0, w/2, h/2, w * 0.7);
      bgGradient.addColorStop(0, '#0a0a12');
      bgGradient.addColorStop(1, '#050508');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, w, h);
      
      // RETRO-WAVE GRID
      drawRetroGrid();
      
      // ============================================
      // OPPONENT GHOST VIEW (Top 25%)
      // ============================================
      
      // Opponent zone background (darker, ghostly)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.fillRect(0, 0, w, opponentZoneH);
      
      // Flash effect when opponent hits bomb
      if (opponentFlashRef.current.active && now < opponentFlashRef.current.until) {
        const flashProgress = (opponentFlashRef.current.until - now) / 500;
        ctx.fillStyle = `rgba(255, 0, 0, ${flashProgress * 0.5})`;
        ctx.fillRect(0, 0, w, opponentZoneH);
      } else {
        opponentFlashRef.current.active = false;
      }
      
      // Draw ghost hits (opponent actions)
      ghostHitsRef.current = ghostHitsRef.current.filter(ghost => {
        const age = now - ghost.time;
        if (age > 800) return false; // Remove after 800ms
        
        const alpha = 1 - (age / 800);
        const scale = 1 + (age / 400);
        const x = ghost.x * dpr;
        const y = ghost.y * dpr;
        
        // Spark/explosion effect
        ctx.globalAlpha = alpha;
        
        // Outer ring (ensure positive radius)
        const outerRadius = Math.max(1, 20 * scale * dpr);
        ctx.strokeStyle = ghost.color;
        ctx.lineWidth = 3 * dpr;
        ctx.shadowColor = ghost.color;
        ctx.shadowBlur = 15 * dpr;
        ctx.beginPath();
        ctx.arc(x, y, outerRadius, 0, Math.PI * 2);
        ctx.stroke();
        
        // Inner spark (ensure positive radius)
        const innerRadius = Math.max(1, 5 * Math.max(0.1, 2 - scale) * dpr);
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(x, y, innerRadius, 0, Math.PI * 2);
        ctx.fill();
        
        // Points indicator
        if (ghost.type === 'hit') {
          ctx.font = `bold ${14 * dpr}px Anton`;
          ctx.fillStyle = ghost.color;
          ctx.textAlign = 'center';
          ctx.fillText('+', x, y - 25 * dpr);
        } else if (ghost.type === 'bomb') {
          ctx.font = `bold ${14 * dpr}px Anton`;
          ctx.fillStyle = '#FF00FF';
          ctx.textAlign = 'center';
          ctx.fillText('💀', x, y - 25 * dpr);
        }
        
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
        return true;
      });
      
      // "OPPONENT" label
      ctx.font = `bold ${12 * dpr}px Anton`;
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.textAlign = 'center';
      ctx.fillText('⚔️ OPPONENT ZONE ⚔️', w / 2, 20 * dpr);
      
      // ============================================
      // VS DIVIDER BAR
      // ============================================
      const vsGradient = ctx.createLinearGradient(0, vsBarY - 15 * dpr, 0, vsBarY + 15 * dpr);
      vsGradient.addColorStop(0, '#FF000000');
      vsGradient.addColorStop(0.5, '#FF0000');
      vsGradient.addColorStop(1, '#FF000000');
      ctx.fillStyle = vsGradient;
      ctx.fillRect(0, vsBarY - 15 * dpr, w, 30 * dpr);
      
      // VS text
      ctx.font = `bold ${24 * dpr}px Anton`;
      ctx.fillStyle = '#FFFFFF';
      ctx.strokeStyle = '#FF0000';
      ctx.lineWidth = 3 * dpr;
      ctx.textAlign = 'center';
      ctx.shadowColor = '#FF0000';
      ctx.shadowBlur = 10 * dpr;
      ctx.strokeText('— VS —', w / 2, vsBarY + 8 * dpr);
      ctx.fillText('— VS —', w / 2, vsBarY + 8 * dpr);
      ctx.shadowBlur = 0;

      // Update golden elon rotation
      goldenElonAngleRef.current += 0.08;

      // ---- DRAW TARGETS (Player Zone - Bottom 75%) ----
      const targets = Object.values(targetsRef.current);
      
      targets.forEach(target => {
        const visual = TARGET_VISUALS[target.type] || TARGET_VISUALS.gem;
        const age = now - target.spawnTime;
        const lifeProgress = age / target.lifetime;
        
        // Spawn animation progress (first 200ms)
        const spawnProgress = Math.min(1, age / 200);
        
        // Fade out in last 30% of life
        const alpha = lifeProgress > 0.7 ? 1 - (lifeProgress - 0.7) / 0.3 : 1;
        
        // Pulse phase for animations
        const pulsePhase = now / 200;

        // Moving targets (Golden Elon)
        if (target.is_moving) {
          target.x += target.velocity_x;
          target.y += target.velocity_y;
          if (target.x < target.size || target.x > width - target.size) target.velocity_x *= -1;
          if (target.y < target.size || target.y > height - target.size) target.velocity_y *= -1;
        }

        ctx.globalAlpha = alpha;

        let finalRadius;
        
        // GOLDEN ELON - SPINNING GOLD COIN
        if (target.type === 'golden_elon') {
          finalRadius = drawGoldCoin(
            target.x, target.y, target.size,
            goldenElonAngleRef.current,
            spawnProgress
          );
          
          // Star emoji
          const emojiSize = Math.floor(finalRadius * 1.2 * dpr);
          ctx.font = `${emojiSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('⭐', target.x * dpr, target.y * dpr);
          
          // "JACKPOT!" label
          ctx.font = `bold ${16 * dpr}px Anton`;
          ctx.fillStyle = '#FFD700';
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 4 * dpr;
          ctx.shadowColor = '#FFD700';
          ctx.shadowBlur = 10 * dpr;
          const labelY = (target.y - target.size - 20) * dpr;
          ctx.strokeText('JACKPOT!', target.x * dpr, labelY);
          ctx.fillText('JACKPOT!', target.x * dpr, labelY);
          ctx.shadowBlur = 0;
          
        } else {
          // GLASS ORB TARGETS
          finalRadius = drawGlassOrb(
            target.x, target.y, target.size,
            visual.color, visual.glowColor, visual.orbStyle,
            spawnProgress, pulsePhase
          );
          
          // Emoji inside orb
          const emojiSize = Math.floor(finalRadius * 0.8 * dpr);
          ctx.font = `${emojiSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(visual.emoji, target.x * dpr, target.y * dpr);
        }

        ctx.globalAlpha = 1;
      });

      // ---- DRAW PARTICLES ----
      particlesRef.current = particlesRef.current.filter(p => {
        p.update();
        p.draw(ctx, dpr);
        return p.life > 0;
      });

      // ---- FROZEN OVERLAY ----
      if (frozenUntilRef.current > now) {
        ctx.fillStyle = 'rgba(128, 0, 255, 0.4)';
        ctx.fillRect(0, 0, w, h);
        
        // Frozen text
        ctx.font = `bold ${48 * dpr}px Anton`;
        ctx.fillStyle = '#FF00FF';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 4 * dpr;
        ctx.textAlign = 'center';
        ctx.shadowColor = '#FF00FF';
        ctx.shadowBlur = 20 * dpr;
        ctx.strokeText('💀 FROZEN! 💀', w / 2, h / 2);
        ctx.fillText('💀 FROZEN! 💀', w / 2, h / 2);
        ctx.shadowBlur = 0;
      }
      
      // Difficulty phase indicator
      if (difficultyPhaseRef.current === 'escalation' || difficultyPhaseRef.current === 'doom') {
        const phaseText = difficultyPhaseRef.current === 'doom' ? '🔥 DOOM MODE 🔥' : '⚡ ESCALATION ⚡';
        const phaseColor = difficultyPhaseRef.current === 'doom' ? '#FF0000' : '#FFA500';
        ctx.font = `bold ${14 * dpr}px Anton`;
        ctx.fillStyle = phaseColor;
        ctx.textAlign = 'center';
        ctx.shadowColor = phaseColor;
        ctx.shadowBlur = 10 * dpr;
        ctx.fillText(phaseText, w / 2, h - 20 * dpr);
        ctx.shadowBlur = 0;
      }

      const totalShakeRestore = shakeRef.current.intensity + (difficultyPhaseRef.current === 'doom' ? 2 : 0);
      if (totalShakeRestore > 0) ctx.restore();
      animationRef.current = requestAnimationFrame(render);
    };

    animationRef.current = requestAnimationFrame(render);
    
    return () => {
      cancelAnimationFrame(animationRef.current);
      resizeObserver.disconnect();
    };
  }, [gameState]);

  const joinGame = useCallback(async () => {
    if (userDegen < currentEntryFee) {
      toast.error(`Need ${currentEntryFee.toLocaleString()} $SLAP!`);
      return;
    }

    // ============================================================
    // CRITICAL: REAL ON-CHAIN TOKEN TRANSFER TO TREASURY
    // Game ONLY starts after successful blockchain confirmation!
    // ============================================================
    
    // Get user's secret key for signing
    const userSecretKey = user?.secretKey || localWallet?.secretKey;
    const userPublicKey = user?.wallet_address || localWallet?.publicKey;
    
    if (!userSecretKey || !userPublicKey) {
      toast.error('Wallet not connected!', {
        description: 'Please reconnect your wallet'
      });
      return;
    }
    
    // Show payment processing toast
    const loadingToast = toast.loading('Processing entry fee...', {
      description: 'Transferring $SLAP to Treasury...'
    });
    
    try {
      // Import the transfer service
      const { transferToTreasury } = await import('../services/tokenTransferService');
      
      // Execute REAL blockchain transfer
      const transferResult = await transferToTreasury({
        amount: currentEntryFee,
        userPublicKey: userPublicKey,
        secretKeyBase58: userSecretKey,
        onProgress: (progress) => {
          toast.loading(`${progress.message}`, { id: loadingToast });
        }
      });
      
      // CRITICAL: Check if transfer was successful
      if (!transferResult.success) {
        toast.error('Entry Fee Transfer Failed!', {
          id: loadingToast,
          description: transferResult.error || 'Transaction failed'
        });
        return; // DO NOT START GAME
      }
      
      // Payment successful!
      toast.success('Entry Fee Paid! ✅', {
        id: loadingToast,
        description: `TX: ${transferResult.signature?.slice(0, 8)}...`
      });
      
      console.log('[DegenSlap] Entry fee paid:', transferResult.signature);
      
      // Refresh balance after payment (use the hook function)
      await fetchSlapTokenBalance(true);
      
    } catch (error) {
      console.error('[DegenSlap] Payment error:', error);
      toast.error('Payment Failed!', {
        id: loadingToast,
        description: error.message || 'Unknown error'
      });
      return; // DO NOT START GAME
    }
    
    // ============================================================
    // PAYMENT CONFIRMED - NOW START THE GAME
    // ============================================================

    // Initialize card buffs for this game
    const buff = initializeGameBuffs();
    activeBuffRef.current = buff;
    slowMoFactorRef.current = getSlowMoFactor();
    setBuffDisplayInfo(getBuffDisplayInfo());
    
    if (buff) {
      toast.success(`${buff.icon} ${buff.name} ACTIVE!`, { 
        duration: 2000,
        style: { background: buff.color + '30', border: `2px solid ${buff.color}` }
      });
      if (soundEnabled) audioManager.play('shieldActivate');
    }

    // Reset state
    botScoresRef.current = { bot1: 0, bot2: 0, bot3: 0 };
    targetsRef.current = {};
    particlesRef.current = [];
    ghostHitsRef.current = []; // Reset ghost hits
    scoreRef.current = 0;
    spawnRateRef.current = 600; // Reset spawn rate
    difficultyPhaseRef.current = 'normal';
    setDifficultyPhase('normal');
    setScore(0);
    setPot(currentEntryFee * 4);
    setShowVictoryScreen(false);
    setLeaderboard([
      { rank: 1, username, score: 0, sid: 'player', is_leader: true },
      { rank: 2, username: 'Bot_1', score: 0, sid: 'bot1', is_leader: false },
      { rank: 3, username: 'Bot_2', score: 0, sid: 'bot2', is_leader: false },
      { rank: 4, username: 'Bot_3', score: 0, sid: 'bot3', is_leader: false },
    ]);
    setPlayerCount(4);
    setGameState('countdown');

    // Countdown with new audio
    let count = 3;
    const interval = setInterval(() => {
      setCountdown(count);
      if (soundEnabled) audioManager.play('countdown');
      count--;
      if (count < 0) {
        clearInterval(interval);
        setCountdown('GO!');
        if (soundEnabled) audioManager.play('countdownGo');
        setTimeout(() => {
          setCountdown(null);
          setTimeRemaining(30);
          gameActiveRef.current = true;
          setGameState('playing');
          
          // Target spawning with PROGRESSIVE DIFFICULTY
          const targetTypes = [
            { type: 'gem', points: 100, size: 45 },
            { type: 'rocket', points: 150, size: 50 },
            { type: 'pepe_coin', points: 100, size: 45 },
            { type: 'diamond', points: 120, size: 42 },
            { type: 'sec_logo', points: -150, size: 50 },
            { type: 'gary_gensler', points: -200, size: 55 },
            { type: 'red_candle', points: -100, size: 40 },
            { type: 'rug_pull', points: 0, size: 55, freeze: true },
            { type: 'golden_elon', points: 500, size: 80, jackpot: true },
          ];

          const endTime = Date.now() + 30000;
          const gameStartTime = Date.now();
          
          // PROGRESSIVE DIFFICULTY: "The Panic Curve"
          const getDifficultySettings = () => {
            const elapsed = (Date.now() - gameStartTime) / 1000;
            
            if (elapsed >= 25) {
              // DOOM MODE: 25-30 seconds
              if (difficultyPhaseRef.current !== 'doom') {
                difficultyPhaseRef.current = 'doom';
                setDifficultyPhase('doom');
                toast('🔥 DOOM MODE!', { duration: 1500, style: { background: '#FF0000', color: 'white' } });
              }
              return { spawnDelay: 200, speedMultiplier: 2.0, lifetimeMultiplier: 0.6 };
            } else if (elapsed >= 15) {
              // ESCALATION: 15-25 seconds
              if (difficultyPhaseRef.current !== 'escalation') {
                difficultyPhaseRef.current = 'escalation';
                setDifficultyPhase('escalation');
                toast('⚡ ESCALATION!', { duration: 1500, style: { background: '#FFA500', color: 'black' } });
              }
              return { spawnDelay: 400, speedMultiplier: 1.5, lifetimeMultiplier: 0.8 };
            } else {
              // NORMAL: 0-15 seconds
              return { spawnDelay: 600, speedMultiplier: 1.0, lifetimeMultiplier: 1.0 };
            }
          };
          
          const spawnTarget = () => {
            if (!gameActiveRef.current) return;
            
            const { spawnDelay, speedMultiplier, lifetimeMultiplier } = getDifficultySettings();

            let selectedType;
            const rand = Math.random();
            if (rand < 0.04) selectedType = targetTypes.find(t => t.type === 'golden_elon');
            else if (rand < 0.12) selectedType = targetTypes.find(t => t.type === 'rug_pull');
            else if (rand < 0.40) {
              const badTargets = targetTypes.filter(t => t.points < 0 && !t.freeze);
              selectedType = badTargets[Math.floor(Math.random() * badTargets.length)];
            } else {
              const goodTargets = targetTypes.filter(t => t.points > 0 && !t.jackpot);
              selectedType = goodTargets[Math.floor(Math.random() * goodTargets.length)];
            }

            // Spawn in player zone (bottom 75% of canvas)
            const { width, height } = canvasSizeRef.current;
            const playerZoneTop = height * 0.3; // Below VS bar
            const playerZoneHeight = height * 0.65;
            
            // Apply slow-mo factor from card buff to lifetimes (longer = easier to hit)
            const buffSlowMo = slowMoFactorRef.current;
            const lifetimeBonus = buffSlowMo < 1 ? 1 / buffSlowMo : 1; // Inverse: 0.6 slowmo = 1.67x longer lifetime
            
            const target = {
              id: `t_${Date.now()}_${Math.random()}`,
              ...selectedType,
              x: Math.random() * (width - 120) + 60,
              y: playerZoneTop + Math.random() * playerZoneHeight,
              spawnTime: Date.now(),
              lifetime: (selectedType.type === 'golden_elon' ? 2500 : 900 + Math.random() * 600) * lifetimeMultiplier * lifetimeBonus,
              is_moving: selectedType.type === 'golden_elon',
              velocity_x: selectedType.type === 'golden_elon' ? (Math.random() - 0.5) * 6 * speedMultiplier * buffSlowMo : 0,
              velocity_y: selectedType.type === 'golden_elon' ? (Math.random() - 0.5) * 4 * speedMultiplier * buffSlowMo : 0,
            };

            targetsRef.current[target.id] = target;
            
            setTimeout(() => {
              if (targetsRef.current[target.id]) {
                delete targetsRef.current[target.id];
              }
            }, target.lifetime);
            
            if (gameActiveRef.current) {
              // Use difficulty-adjusted spawn delay
              const jitter = spawnDelay * 0.3;
              setTimeout(spawnTarget, spawnDelay + (Math.random() - 0.5) * jitter);
            }
          };

          // Timer + Bot AI with Ghost View
          const timerInterval = setInterval(() => {
            const remaining = Math.ceil((endTime - Date.now()) / 1000);
            setTimeRemaining(Math.max(0, remaining));

            // Bot scoring with Ghost View feedback
            if (Math.random() < 0.35) {
              const bot = ['bot1', 'bot2', 'bot3'][Math.floor(Math.random() * 3)];
              const hitSuccess = Math.random() < 0.65;
              const hitBomb = Math.random() < 0.08;
              
              if (hitBomb) {
                // Bot hit a bomb - freeze them and show on ghost view
                simulateOpponentHit('bomb');
                botScoresRef.current[bot] -= 50;
              } else if (hitSuccess) {
                botScoresRef.current[bot] += 100;
                simulateOpponentHit('hit');
              } else {
                botScoresRef.current[bot] -= 100;
                simulateOpponentHit('miss');
              }
            }

            // Update leaderboard live
            const currentScores = [
              { sid: 'player', username, score: scoreRef.current },
              { sid: 'bot1', username: 'Bot_1', score: botScoresRef.current.bot1 },
              { sid: 'bot2', username: 'Bot_2', score: botScoresRef.current.bot2 },
              { sid: 'bot3', username: 'Bot_3', score: botScoresRef.current.bot3 },
            ].sort((a, b) => b.score - a.score);
            setLeaderboard(currentScores.map((p, i) => ({ ...p, rank: i + 1, is_leader: i === 0 })));

            if (remaining <= 0) {
              clearInterval(timerInterval);
              gameActiveRef.current = false;
              difficultyPhaseRef.current = 'normal';
              
              const winner = currentScores[0];
              const calculatedPot = currentEntryFee * 4;
              const payout = Math.floor(calculatedPot * 0.9);

              setGameState('finished');
              setResult({ winner_sid: winner.sid, winner_username: winner.username, payout, pot: calculatedPot, playerScore: scoreRef.current });
              setShowVictoryScreen(true);
              
              // Victory/defeat sounds handled by VictoryScreen component
            }
          }, 1000);

          spawnTarget();
        }, 500);
      }
    }, 1000);
  }, [username, userDegen, soundEnabled, currentEntryFee, initializeGameBuffs, getSlowMoFactor, getBuffDisplayInfo, simulateOpponentHit, user, localWallet, fetchSlapTokenBalance]);

  const handleCanvasClick = useCallback((e) => {
    if (gameState !== 'playing' || Date.now() < frozenUntilRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const { width, height } = canvasSizeRef.current;
    
    // Convert screen coordinates to logical coordinates
    const x = (e.clientX - rect.left) * (width / rect.width);
    const y = (e.clientY - rect.top) * (height / rect.height);

    let hitTarget = null;
    for (const target of Object.values(targetsRef.current)) {
      const dist = Math.sqrt((target.x - x) ** 2 + (target.y - y) ** 2);
      if (dist <= target.size * 1.2) { // Slightly larger hitbox for mobile
        hitTarget = target;
        break;
      }
    }

    if (hitTarget) {
      delete targetsRef.current[hitTarget.id];
      const visual = TARGET_VISUALS[hitTarget.type] || TARGET_VISUALS.gem;

      if (hitTarget.freeze) {
        // Check if shield can block the freeze
        if (consumeShield()) {
          // Shield absorbed the bomb!
          if (soundEnabled) audioManager.play('shieldPop');
          shakeRef.current.intensity = 10;
          particlesRef.current.push(new ScoreParticle(x, y, '#00CCFF', '🛡️ BLOCKED!'));
          // Cyan shield pop particles
          for (let i = 0; i < 20; i++) {
            particlesRef.current.push(new ShatterParticle(x, y, '#00CCFF', (i / 20) * Math.PI * 2));
            particlesRef.current.push(new ExplosionParticle(x, y, '#00CCFF'));
          }
          // Update buff display
          setBuffDisplayInfo(getBuffDisplayInfo());
        } else {
          // No shield - get frozen
          if (soundEnabled) audioManager.play('freeze');
          frozenUntilRef.current = Date.now() + 2000;
          setIsFrozen(true);
          setTimeout(() => setIsFrozen(false), 2000);
          shakeRef.current.intensity = 25;
          particlesRef.current.push(new ScoreParticle(x, y, '#FF00FF', '💀 FROZEN!'));
          // Purple shatter effect
          for (let i = 0; i < 15; i++) {
            particlesRef.current.push(new ShatterParticle(x, y, '#FF00FF', (i / 15) * Math.PI * 2));
          }
        }
      } else if (hitTarget.jackpot) {
        if (soundEnabled) audioManager.play('jackpot');
        const basePoints = hitTarget.points;
        const finalPoints = applyScoreMultiplier(basePoints);
        setScore(prev => prev + finalPoints);
        // MASSIVE gold explosion
        for (let i = 0; i < 50; i++) {
          particlesRef.current.push(new ExplosionParticle(x, y, '#FFD700'));
          particlesRef.current.push(new ShatterParticle(x, y, '#FFD700', (i / 50) * Math.PI * 2));
        }
        particlesRef.current.push(new ScoreParticle(x, y, '#FFD700', `+${finalPoints} JACKPOT!`));
        shakeRef.current.intensity = 25;
      } else if (hitTarget.points < 0) {
        // Bad target hit
        if (soundEnabled) audioManager.play('bombHit');
        setScore(prev => prev + hitTarget.points);
        shakeRef.current.intensity = 15;
        particlesRef.current.push(new ScoreParticle(x, y, '#FF4444', hitTarget.points.toString()));
        // Red shatter
        for (let i = 0; i < 12; i++) {
          particlesRef.current.push(new ShatterParticle(x, y, '#FF3333', (i / 12) * Math.PI * 2));
        }
      } else {
        // GOOD HIT - Green shatter effect with multiplier
        if (soundEnabled) {
          audioManager.play('tapGood');
          if (activeBuffRef.current?.scoreMultiplier > 1) {
            audioManager.play('multiplierHit');
          }
        }
        const basePoints = hitTarget.points;
        const finalPoints = applyScoreMultiplier(basePoints);
        setScore(prev => prev + finalPoints);
        
        // Show multiplied score if buff active
        if (activeBuffRef.current?.scoreMultiplier > 1) {
          particlesRef.current.push(new ScoreParticle(x, y, activeBuffRef.current.color || '#00FF88', `+${finalPoints}`));
        } else {
          particlesRef.current.push(new ScoreParticle(x, y, '#00FF88', `+${finalPoints}`));
        }
        
        // Glass shatter particles
        for (let i = 0; i < 15; i++) {
          particlesRef.current.push(new ShatterParticle(x, y, visual.glowColor, (i / 15) * Math.PI * 2));
        }
        // Plus some glow particles
        for (let i = 0; i < 8; i++) {
          particlesRef.current.push(new ExplosionParticle(x, y, visual.color));
        }
      }
    } else {
      // Miss (tap on empty space)
      if (soundEnabled) audioManager.play('tapSuccess');
      shakeRef.current.intensity = 2;
    }

    if (navigator.vibrate) navigator.vibrate(30);
  }, [gameState, soundEnabled, consumeShield, applyScoreMultiplier, getBuffDisplayInfo]);

  // Handle touch events for mobile
  const handleCanvasTouch = useCallback((e) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleCanvasClick({ clientX: touch.clientX, clientY: touch.clientY });
  }, [handleCanvasClick]);

  return (
    <div className="h-screen bg-[#050505] flex flex-col overflow-hidden" data-testid="degen-slap-container">
      
      {/* === LOBBY STATE === */}
      {gameState === 'lobby' && (
        <div className="flex-1 flex flex-col">
          {/* Header with Live Price */}
          <header className="p-3 flex items-center justify-between border-b border-white/10">
            <Button onClick={() => navigate('/')} variant="ghost" size="sm" className="text-white/70">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            
            {/* Center: Live Price Display */}
            <LivePriceDisplay />
            
            <div className="flex items-center gap-2">
              <Button onClick={toggleSound} variant="ghost" size="sm" className="text-white/70">
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
              <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/50">
                <Coins className="w-4 h-4 text-yellow-400" />
                <span className="font-bold text-yellow-400">{userDegen.toLocaleString()}</span>
              </div>
            </div>
          </header>

          {/* Main Lobby Content */}
          <div className="flex-1 flex flex-col items-center justify-center p-4 overflow-y-auto">
            {/* Title */}
            <motion.h1 
              className="font-anton text-4xl md:text-5xl mb-4 text-center"
              style={{
                background: 'linear-gradient(180deg, #FFD700 0%, #FF6B00 50%, #FF0000 100%)',
                WebkitBackgroundClip: 'text', 
                WebkitTextFillColor: 'transparent',
              }}
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              SLAP BATTLES
            </motion.h1>

            {/* Compact How-to-Play Toggle */}
            {showTutorial && (
              <motion.div 
                className="bg-black/60 border border-white/10 rounded-xl px-4 py-3 max-w-md w-full mb-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="flex items-center justify-between gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">💎🐸🚀</span>
                    <span className="text-green-400">TAP</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🏛️👴📉</span>
                    <span className="text-red-400">AVOID</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">⭐</span>
                    <span className="text-yellow-400">+500!</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* NEW: Wager Card Component with Dynamic Pricing */}
            <WagerCard
              arenaType="casual"
              userShards={userDegen}
              connected={connected}
              onPlay={joinGame}
              onClaimShards={claimTestDegen}
              claimingShards={claimingShards}
              pumpFunUrl="https://pump.fun"
            />
          </div>
        </div>
      )}

      {/* === COUNTDOWN STATE === */}
      {gameState === 'countdown' && (
        <div className="flex-1 flex items-center justify-center bg-black">
          <motion.div 
            className="font-anton text-[180px] md:text-[250px]"
            style={{
              background: 'linear-gradient(180deg, #FFD700 0%, #FF6B00 100%)',
              WebkitBackgroundClip: 'text', 
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 60px rgba(255, 165, 0, 0.5)',
            }}
            key={countdown}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 1.5, opacity: 0 }}
          >
            {countdown}
          </motion.div>
        </div>
      )}

      {/* === PLAYING / FINISHED STATE === */}
      {(gameState === 'playing' || gameState === 'finished') && (
        <div className="flex-1 flex flex-col">
          
          {/* TOP HUD - 30% */}
          <div className="h-[30%] min-h-[120px] bg-gradient-to-b from-black to-transparent p-3 flex flex-col">
            
            {/* Top Row: Back, Title, Sound */}
            <div className="flex items-center justify-between mb-2">
              <Button onClick={() => navigate('/')} variant="ghost" size="sm" className="text-white/50 p-1">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <span className="font-anton text-lg text-yellow-400">SLAP BATTLES</span>
              <Button onClick={toggleSound} variant="ghost" size="sm" className="text-white/50 p-1">
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
            </div>

            {/* Main HUD Row */}
            <div className="flex-1 flex items-center justify-between gap-2">
              
              {/* Left: Compact Leaderboard */}
              <div className="flex-1 max-w-[140px] bg-black/60 rounded-lg p-2 border border-white/10">
                <div className="flex items-center gap-1 mb-1">
                  <Trophy className="w-3 h-3 text-yellow-400" />
                  <span className="text-[10px] text-white/50 uppercase">Rankings</span>
                </div>
                <div className="space-y-0.5">
                  {leaderboard.slice(0, 4).map((player) => (
                    <div 
                      key={player.sid} 
                      className={`flex items-center justify-between text-xs px-1 py-0.5 rounded ${
                        player.sid === 'player' ? 'bg-cyan-500/20' : ''
                      }`}
                    >
                      <span className={`truncate max-w-[60px] ${player.is_leader ? 'text-yellow-400' : 'text-white/70'}`}>
                        {player.is_leader && '👑'}{player.username.slice(0, 6)}
                      </span>
                      <span className={`font-bold ${player.score >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {player.score}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Center: HUGE POT + Score */}
              <div className="flex-1 text-center">
                <motion.div 
                  className="font-anton text-3xl md:text-5xl"
                  style={{
                    background: 'linear-gradient(180deg, #FFD700 0%, #FFA500 100%)',
                    WebkitBackgroundClip: 'text', 
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 0 10px rgba(255, 165, 0, 0.5))',
                  }}
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                >
                  💰 {pot}
                </motion.div>
                <div className="text-sm text-white/50">POT</div>
                
                {/* Your Score */}
                <div className="mt-1">
                  <span className="text-white/50 text-xs">YOUR SCORE: </span>
                  <span className={`font-anton text-xl ${score >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>
                    {score}
                  </span>
                </div>
              </div>

              {/* Right: Timer */}
              <div className="flex-1 max-w-[100px] text-right">
                <motion.div 
                  className={`font-anton text-5xl md:text-6xl ${
                    timeRemaining <= 5 ? 'text-red-500' : 'text-yellow-400'
                  }`}
                  animate={timeRemaining <= 5 ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.5, repeat: Infinity }}
                >
                  {timeRemaining}
                </motion.div>
                <div className="text-xs text-white/50 uppercase">Seconds</div>
              </div>
            </div>
          </div>

          {/* GAME ARENA - 70% - Clean canvas, no overlays */}
          <div ref={containerRef} className="h-[70%] relative bg-[#050508]">
            {/* Buff HUD - Shows active card buffs */}
            <BuffHUD buffInfo={buffDisplayInfo} isGameActive={gameState === 'playing'} />
            
            <canvas 
              ref={canvasRef} 
              onClick={handleCanvasClick}
              onTouchStart={handleCanvasTouch}
              className={`w-full h-full touch-none ${isFrozen ? 'border-4 border-purple-500' : ''}`}
              style={{ 
                touchAction: 'none',
                filter: isFrozen ? 'hue-rotate(180deg) saturate(1.5)' : 'none',
                imageRendering: 'crisp-edges'
              }}
              data-testid="game-canvas"
            />
          </div>
        </div>
      )}

      {/* === VICTORY SCREEN (The Payday) === */}
      <VictoryScreen
        isOpen={showVictoryScreen}
        isWinner={result?.winner_sid === 'player'}
        payout={result?.payout}
        winnerUsername={result?.winner_username}
        playerScore={result?.playerScore || score}
        equippedCardName={equippedCard?.character_name}
        onPlayAgain={() => {
          setShowVictoryScreen(false);
          setGameState('lobby');
          setResult(null);
          setShowTutorial(false);
        }}
        onExit={() => navigate('/')}
      />
    </div>
  );
};

export default DegenSlap;
